import zIndex from '@mui/material/styles/zIndex';
import React, { useState } from 'react';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { CircularProgress, Alert } from '@mui/material';

const SubscriptionList = () => {
    const [isHovered, setIsHovered] = useState(false);
    
    const { data: subscriptionData, isLoading, error } = useSubscriptions({
        page: 1,
        limit: 20,
        status: 'active'
    });

    // Transform real subscription data to match expected format
    const subscriptions = subscriptionData?.subscriptions?.map((sub, index) => ({
        id: sub._id,
        name: sub.productName || `Subscription ${index + 1}`,
        icon: getIconForProduct(sub.productName || '')
    })) || [];

    // Helper function to map product names to icons
    const getIconForProduct = (productName) => {
        const name = productName.toLowerCase();
        if (name.includes('project')) return 'triangleIcon';
        if (name.includes('pitch')) return 'documentIcon';
        if (name.includes('art')) return 'letterIcon';
        if (name.includes('sign')) return 'upArrowIcon';
        if (name.includes('file')) return 'serverIcon';
        if (name.includes('launch')) return 'triangleIcon';
        if (name.includes('crew')) return 'documentIcon';
        if (name.includes('chat')) return 'chatIcon';
        if (name.includes('campaign')) return 'upArrowIcon';
        if (name.includes('crm')) return 'letterIcon';
        if (name.includes('care')) return 'triangleIcon';
        if (name.includes('news')) return 'chatIcon';
        if (name.includes('mail')) return 'documentIcon';
        return 'triangleIcon'; // default
    };

    // Define icon components - these would normally be imported from files
    // Using SVG shapes to resemble your provided blue gradient icons
    const getIcon = (iconName) => {
        const iconStyle = {
            width: '16px',
            height: '16px',
            fill: 'url(#blueGradient)',
            flexShrink: 0
        };

        switch (iconName) {
            case 'triangleIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M12 2L2 22h20L12 2z" />
                    </svg>
                );
            case 'documentIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1v5h5v10H6V3h7zm-2 8H8v1h3v-1zm0 2H8v1h3v-1zm0 2H8v1h3v-1zm5-2h-3v1h3v-1zm0 2h-3v1h3v-1z" />
                    </svg>
                );
            case 'letterIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                    </svg>
                );
            case 'upArrowIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
                    </svg>
                );
            case 'serverIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v5c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 9H4c-1.1 0-2 .9-2 2v5c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-5c0-1.1-.9-2-2-2zM4 7h16V4H4v3zm0 12h16v-3H4v3z" />
                    </svg>
                );
            case 'chatIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
                    </svg>
                );
            case 'chatVideoIcon':
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 10h2v2H7v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z" />
                    </svg>
                );
            default:
                return (
                    <svg viewBox="0 0 24 24" style={iconStyle}>
                        <defs>
                            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#1e4fd0" />
                                <stop offset="100%" stopColor="#45b3e0" />
                            </linearGradient>
                        </defs>
                        <circle cx="12" cy="12" r="8" />
                    </svg>
                );
        }
    };

    // Container styles
    const containerStyle = {
        position: 'relative',
        width: '220px',
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        paddingLeft: '12px',
        color: 'white',
        // backgroundColor: '#1a1a1a',
        // fontFamily: 'Arial, sans-serif',
    };

    // Title styles
    const titleStyle = {
        fontSize: '16px',
        fontWeight: 500,
        marginBottom: '8px',
    };

    // Icon bar styles - when not hovered
    const iconBarStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginBottom: '4px',
    };

    // Icon styles for both compact and expanded views
    const iconContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        borderRadius: '4px',
        width: '24px',
        height: '24px',
        padding: '4px'
    };

    // Expanded dropdown styles
    const expandedListStyle = {
        position: 'absolute',
        left: '12px',
        top: '0',
        width: '180px',
        backgroundColor: '#1a1a1a',
        zIndex  : 1000,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        // zIndex: 10,
        display: isHovered ? 'block' : 'none',
    };

    // Item styles for expanded list
    const listItemStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 0',
        fontSize: '14px',
    };

    // // Count badge style
    // const countBadgeStyle = {
    //     backgroundColor: '#3ea6ff',
    //     color: 'black',
    //     borderRadius: '4px',
    //     padding: '2px 4px',
    //     fontSize: '12px',
    //     fontWeight: 'bold',
    // };

    // For the "+" icon showing more items
    const plusIconStyle = {
        ...iconContainerStyle,
        backgroundColor: 'transparent',
        color: '#3ea6ff',
        fontWeight: 'bold',
    };

    if (isLoading) {
        return (
            <div style={containerStyle}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                    <CircularProgress size={24} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={containerStyle}>
                <Alert severity="error" style={{ fontSize: '12px', padding: '8px' }}>
                    Failed to load subscriptions
                </Alert>
            </div>
        );
    }

    if (!subscriptions.length) {
        return (
            <div style={containerStyle}>
                <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' }}>
                    No active subscriptions
                </div>
            </div>
        );
    }

    return (
        <div
            style={containerStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >

            {/* Compact icon bar - shows only first 5 icons plus "+" if there are more */}
            <div style={iconBarStyle}>
                {subscriptions.slice(0, 5).map((sub) => (
                    <div key={sub.id} style={iconContainerStyle}>
                        {getIcon(sub.icon)}
                    </div>
                ))}

                {subscriptions.length > 5 && (
                    <div style={plusIconStyle}>
                        +{subscriptions.length - 5}
                    </div>
                )}
            </div>

            {/* Expanded dropdown that shows on hover */}
            <div style={expandedListStyle}>
                <div style={titleStyle}>Subscriptions</div>

                {subscriptions.map((sub) => (
                    <div key={sub.id} style={listItemStyle}>
                        <div style={iconContainerStyle}>
                            {getIcon(sub.icon)}
                        </div>
                        <span>{sub.name}</span>
                    </div>
                ))}

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginTop: '8px',
                    padding: '4px',
                    backgroundColor: '#3ea6ff',
                    color: 'black',
                    alignItems: "center",
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    borderRadius: '4px',
                }}>
                    +6 +10
                </div>
            </div>
        </div>
    );
};

export default SubscriptionList;